import Vue from "vue";

import {
  RunConfigService,
  ConfigService,
  StringService
} from "@nuskin/ns-util";

import { isNullOrEmpty } from "@nuskin/ns-common-lib";

import debounce from "lodash/debounce";

const fallbackImagePath =
  "etc/designs/hello/packaged/img/product-basic__fallback-image.svg";

let NsProductAppService = Vue.prototype.$NsProductAppService;
if (!NsProductAppService) {
  const newEl = document.createElement("div");
  newEl.id = "ns-product-app-service";
  const serviceEl = document.body.appendChild(newEl);

  NsProductAppService = new Vue({
    name: "NsProductAppService",
    data() {
      return {
        runConfig: {},
        runConfigInterval: null,
        runConfigTries: 3,
        locale: "",
        marketConfig: {},
        awsUrl: "",
        siteUrl: "",
        imageFallbackSrc: fallbackImagePath,
        showWholeSalePricing: false,
        loadingConfig: true,
        translations: {},
        loadingTranslations: true
      };
    },
    computed: {
      loading() {
        return this.loadingConfig || this.loadingTranslations;
      }
    },
    async mounted() {
      await this.setConfig();
      this.runConfigInterval = setInterval(async () => {
        if (this.runConfigTries <= 0) {
          clearInterval(this.runConfigInterval);
          this.runConfigTries = 0;
        } else {
          await this.checkConfig();
          this.runConfigTries--;
        }
      }, 350);
    },
    beforeDestroy() {
      clearInterval(this.runConfigInterval);
    },
    methods: {
      /**
       * Gets the full content path for an image or content src
       */
      getContentPath(src) {
        return src.startsWith("/") ? `${this.siteUrl}${src}` : src;
      },

      /**
       * Checks app and market config if current
       */
      checkConfig: debounce(async function(runConfig) {
        runConfig = runConfig || RunConfigService.getRunConfig() || {};
        if (
          !!runConfig.language &&
          !!runConfig.country &&
          (runConfig.language !== this.runConfig.language ||
            runConfig.country !== this.runConfig.country)
        ) {
          await this.setConfig(runConfig);
        }
      }, 350),

      /**
       * Set app and market config, along with associated variables
       */
      async setConfig(runConfig) {
        this.loadingConfig = true;

        runConfig = runConfig || RunConfigService.getRunConfig() || {};
        if (!runConfig.language || !runConfig.country) {
          // the runconfig isn't ready yet
          return;
        }

        this.runConfig = runConfig;
        this.locale = `${this.runConfig.language}_${this.runConfig.country}`;

        this.marketConfig = ConfigService.getMarketConfig();
        if (this.marketConfig) {
          this.awsUrl = this.marketConfig.awsUrl;
          this.siteUrl = this.marketConfig.siteUrl;
          this.imageFallbackSrc = `${this.siteUrl}/${fallbackImagePath}`;
          this.showWholeSalePricing = !!this.marketConfig.showWholeSalePricing;
          await this.setTranslations();
        } else {
          this.awsUrl = null;
          this.siteUrl = null;
          this.imageFallbackSrc = fallbackImagePath;
          this.showWholeSalePricing = false;
        }

        const configData = {
          runConfig: this.runConfig,
          marketConfig: this.marketConfig
        };

        this.loadingConfig = false;
        this.$emit("config-changed", configData);
      },

      /**
       * Set translations from common strings or other common product translations
       */
      async setTranslations() {
        this.loadingTranslations = true;

        const commonStringKeys = [
          { key: "addToAdr", dflt: "Add To Adr" },
          { key: "addToCart", dflt: "Add To Cart" },
          { key: "addToFavorites", dflt: "Add To Favorites" },
          { key: "adrLabel", dflt: this.marketConfig.adrProgram },
          { key: "adrPriceLabel", dflt: "ADR Price" },
          { key: "retailPriceLabel", dflt: "Retail Price" },
          { key: "wholesalePriceLabel", dflt: "Wholesale Price" },
          { key: "salesEventPriceLabel", dflt: "Sale Price" },
          {
            key: "backOrderedWithDate",
            dflt: "Your product(s) will ship by: %date%"
          },
          { key: "discontinued", dflt: "Product Discontinued" },
          { key: "exclusiveOfferTitle", dflt: "Exclusive Offer" },
          {
            key: "exclusiveOfferMessage",
            dflt: "Log in to see if you are qualified to purchase this product."
          },
          {
            key: "exclusiveOfferNoQualification",
            dflt: "You do not have access to purchase this product."
          },
          {
            key: "exclusiveOfferUsedUpQty",
            dflt: "You have reached your purchase limit for this product."
          },
          {
            key: "exclusiveOfferNotAvailable",
            dflt:
              "You are qualified to buy this product, but it is not currently available for purchase."
          },
          { key: "item", dflt: "Item" },
          { key: "quantity", dflt: "Quantity" },
          { key: "notReleasedForSale", dflt: "Coming Soon" },
          { key: "outOfStock", dflt: "Out of stock" },
          { key: "psvDist", dflt: "" },
          { key: "psvCust", dflt: "" },
          { key: "psvPfc", dflt: "" },
          { key: "psvAnon", dflt: "" },
          { key: "reachedPurchaseLimit", dflt: "Reached Purchase Limit" },
          { key: "seeDetails", dflt: "See Details" },
          { key: "signIn", dflt: "Sign In" },
          { key: "stopStatus", dflt: "Currently Not Available" },

          // Quantity Selector
          { key: "done", dflt: "Done" },
          { key: "enter", dflt: "Enter" },
          { key: "maxQuantityExceeded", dflt: "Low stock. Only %stock% left." },
          { key: "select", dflt: "Select" }
        ];

        try {
          const commonStrings = await StringService.getStrings(
            commonStringKeys,
            this.locale
          );

          this.appendTranslations(commonStrings);
        } catch (err) {
          console.warn("Unable to retrieve common strings translations.", err);
        }

        this.loadingTranslations = false;
      },

      /**
       * Append common product translations
       */
      async appendTranslations(translations) {
        if (isNullOrEmpty(translations)) {
          return;
        }

        this.translations = {
          ...this.translations,
          ...translations
        };

        this.$emit("translations-changed", this.translations);
      }
    }
  }).$mount(serviceEl);

  Vue.prototype.$NsProductAppService = NsProductAppService;
}

export default NsProductAppService;
